import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GiftCardCartModel } from '@digilize/shared/definitions/src/lib/models';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { CartHoldBlockers, DeliveryType, EnvConfig, GiftCard } from '@shared/definitions';
import { Observable } from 'rxjs';

import { SKIP_ERROR_INTERCEPTOR } from '../application/errors/error.interceptor';
import { CartDto } from './dto/products.dto';
import { CartSubscriptionCardLineDto } from './dto/subscription.dto';

@Injectable({
  providedIn: 'root',
})
export class CartsApiService {
  private readonly baseUrl = `${this.config.api}/v1.0/carts`;

  constructor(@Inject(ENV_CONFIG) private readonly config: EnvConfig, private readonly client: HttpClient) {}

  setDeliveryMethod(deliveryMethod: DeliveryType, cartId: string): Observable<void> {
    return this.client.put<void>(`${this.baseUrl}/${cartId}/delivery-method?method=${deliveryMethod}`, {});
  }

  getCart(cardId: string, params?: any): Observable<CartDto> {
    return this.client.get<CartDto>(`${this.baseUrl}/${cardId}`, { params });
  }

  /**
   * The subscription cards redeemable on each subscription-ticket line of the cart (PAUL-3557).
   *
   * `SKIP_ERROR_INTERCEPTOR` because this only fills a dropdown: the documented refusals
   * (`cart_not_found`, `shop_context_required`, and `forbidden` on someone else's cart) would
   * otherwise raise a global checkout error on a step the buyer is part-way through, when the
   * dropdown can simply fall back to its "new card" row.
   */
  getSubscriptionCards(cartId: string): Observable<CartSubscriptionCardLineDto[]> {
    return this.client.get<CartSubscriptionCardLineDto[]>(`${this.baseUrl}/${cartId}/subscription-cards`, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
    });
  }

  /**
   * The customer's connected gift cards that can be spent on this cart (PAUL-3491). A guest answers
   * `200` with `[]` rather than `403`, so the call is not conditional on being signed in.
   *
   * `SKIP_ERROR_INTERCEPTOR` for the same reason {@link getSubscriptionCards} uses it: this only
   * fills a list of cards to offer, and its documented refusals (`cart_not_found`,
   * `shop_context_required`, and `forbidden` on someone else's cart) would otherwise raise a global
   * checkout error on a step the buyer is part-way through — when the step can simply fall back to
   * its "type a code" box.
   */
  getGiftCards(cartId: string): Observable<GiftCardCartModel[]> {
    return this.client.get<GiftCardCartModel[]>(`${this.baseUrl}/${cartId}/gift-cards`, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
    });
  }

  addGiftCard(cartId: string, giftCard: GiftCard): Observable<CartDto> {
    return this.client.put<CartDto>(`${this.baseUrl}/${cartId}/gift-cards`, giftCard);
  }

  removeGiftCard(cartId: string, cardId: string): Observable<CartDto> {
    return this.client.delete<CartDto>(`${this.baseUrl}/${cartId}/gift-cards/${cardId}`);
  }

  addDiscount(cartId: string, code: string): Observable<CartDto> {
    return this.client.put<CartDto>(
      `${this.baseUrl}/${cartId}/discount`,
      { code },
      {
        context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
        params: { include: 'hold_blockers' },
      }
    );
  }

  removeDiscount(cartId: string, discountCodeId: string): Observable<CartDto> {
    return this.client.delete<CartDto>(`${this.baseUrl}/${cartId}/discount/${discountCodeId}`);
  }

  addBioscoopcard(cartId: string, code: string, pin: string, externalId: string): Observable<CartDto> {
    return this.client.put<CartDto>(
      `${this.baseUrl}/${cartId}/payment-method`,
      {
        payment_method_code: code,
        payment_method_pin: pin,
        payment_method_external_id: externalId,
      },
      {
        params: {
          include: 'payment_methods',
        },
      }
    );
  }

  removeBioscoopcard(_cartId: string, _cardId: string): Observable<CartDto> {
    return new Observable<CartDto>();
  }

  holdCart(cartId: string): Observable<CartDto> {
    return this.client.put<CartDto>(`${this.baseUrl}/${cartId}/hold`, { hold: true });
  }

  getHoldBlockers(cartId: string): Observable<CartHoldBlockers> {
    return this.client.get<CartHoldBlockers>(`${this.baseUrl}/${cartId}/hold/blockers`);
  }
}
